import React from 'react';
import styled from 'styled-components';
import { Controller } from 'react-hook-form';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { MenuSortOptions } from 'shared/constants';
import { ModalContainer, ModalButton, ModalSecondaryHeader, ModalClose, ModalCopy } from 'shared/modals';
import { Select } from 'shared/components';
import { InputWithMaxLength } from 'shared/components/inputs-with-max-length';
import { useAddCollectionModal } from './use-add-collection-modal';
import { AddCollectionModalProps } from './add-collection-modal.types';
import { AppDisplayTypeSelect } from '../app-display-type-select';
import { MobileAppFlairSelect } from '../mobile-app-flair-select';

export function AddCollectionModal({
  handleAddSection,
  menuSection,
  menuSections,
  onToggle,
  sectionTypeKey,
  products,
}: AddCollectionModalProps): JSX.Element {
  const {
    control,
    handleSubmit,
    collectionId,
    onSubmit,
    handleChangeCollection,
    handleChangeMobileAppDisplayType,
    handleChangeMobileAppFlairType,
    mobileAppDisplayType,
    mobileAppFlairType,
    collectionsToDisplay,
    loading,
    isEditingSection,
  } = useAddCollectionModal({ handleAddSection, menuSection, menuSections, sectionTypeKey, onToggle, products });

  const flags = useFlags();
  const featuredMenuSortOptionEnabled = flags['growth.ecomm.default-as-featured-menu-sort-option.rollout'] ?? false;

  const sortOptions = MenuSortOptions.filter(
    ({ key }) =>
      key !== 'relevance' && key !== 'terpeneshightolow' && (featuredMenuSortOptionEnabled || key !== 'default')
  );

  return (
    <ModalContainer
      isOpen
      padding='40px'
      width='415px'
      alignItems='flex-start'
      justifyContent='flex-start'
      textAlign='left'
    >
      <ModalSecondaryHeader lowercase mb='8px'>
        Add a Collection
      </ModalSecondaryHeader>
      <Copy>Add a custom homepage section from Collections.</Copy>

      <FieldContainer>
        <Label htmlFor='collection'>Collection</Label>
        <Controller
          name='collectionId'
          control={control}
          render={({ value }) => (
            <Select
              value={value}
              disabled={loading || isEditingSection}
              width='100%'
              fontSize={13}
              id='collection'
              onChange={handleChangeCollection}
            >
              <option value='' disabled>
                {loading ? 'Loading...' : 'Select a Collection'}
              </option>
              {collectionsToDisplay.map((collection) => (
                <option key={collection.id} value={collection.id}>
                  {collection.title}
                </option>
              ))}
            </Select>
          )}
        />
      </FieldContainer>

      <Controller
        name='sectionName'
        control={control}
        render={({ onChange, value }) => (
          <InputWithMaxLength
            label='Section Name'
            placeholderText=''
            maxLength={50}
            width={335}
            margin={0}
            id='sectionName'
            value={value}
            onChange={onChange}
          />
        )}
      />

      <FieldContainer>
        <Label htmlFor='sorting'>Sorting</Label>
        <HelpText>Select how products will be sorted in the homepage section.</HelpText>
        <Controller
          name='sorting'
          control={control}
          render={({ onChange, value }) => (
            <Select onChange={onChange} value={value} width='100%' fontSize={13} id='sorting'>
              {sortOptions.map(({ key, label }) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </Select>
          )}
        />
      </FieldContainer>

      <AppDisplayTypeSelect value={mobileAppDisplayType} onChange={handleChangeMobileAppDisplayType} />
      <MobileAppFlairSelect value={mobileAppFlairType} onChange={handleChangeMobileAppFlairType} />

      <ModalButton buttonContainerWidth='auto' mt='0' onClick={handleSubmit(onSubmit)} disabled={!collectionId}>
        Add
      </ModalButton>

      <ModalClose onClick={onToggle} />
    </ModalContainer>
  );
}

const Copy = styled(ModalCopy)`
  margin-bottom: 0;
`;

const Label = styled.label`
  color: #969ea5;
  font-size: 11px;
  font-style: normal;
  font-weight: bold;
  line-height: 150%;
  margin-bottom: 6px;
  text-transform: uppercase;
`;

const HelpText = styled.p`
  color: #6d747b;
  font-size: 12px;
  line-height: ${14 / 12};
  margin-top: -2px;
  margin-bottom: 6px;
`;

const FieldContainer = styled.div`
  margin: 32px 0;
  width: 100%;
`;
